import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import logo from "../../assets/svg/auth/logo.svg";
import { NAV_ICONS, NavIcon } from "./navIcons";
import { useNavigationGuard } from "./UnsavedChanges";
import { ROUTES } from "../../routes/paths";
import { PRODUCT_NAME } from "../../features/billing/data/subscriptionPlans";
import { useCurrentSubscription } from "../../features/billing/hooks/useCurrentSubscription";
import { hasAccess } from "../../features/billing/subscriptionState";
import TrialMeter from "../../features/billing/components/TrialMeter";
import TrialBanner from "../../features/billing/components/TrialBanner";
import { useSignedInUser } from "../../features/auth/hooks/useSignedInUser";
// Deep imports rather than the feature barrels: those pull in every enquiry
// and client screen, and the rail only wants a number from each.
import { useNewEnquiryCount } from "../../features/enquiries/hooks/useNewEnquiryCount";
import { useNewClientCount } from "../../features/clients/hooks/useNewClientCount";
import { MAX_SHOWN_COUNT } from "../../lib/liveNews";
import { NewTag } from "../ui";

interface SidebarItemProps {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  /**
   * How many things behind this item are waiting to be looked at, if it is the
   * kind of item that counts anything. Zero draws nothing — a badge saying "0"
   * is a worse answer than no badge, since it takes up the same space to say
   * there is nothing to say.
   */
  count?: number;
  onClick: () => void;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  icon,
  label,
  active,
  count = 0,
  onClick,
}) => (
  <button
    onClick={onClick}
    aria-current={active ? "page" : undefined}
    // Text and icon keep the same ink in both states — only the pill's fill
    // and hairline border mark the active item. The inactive border is
    // transparent rather than absent so nothing shifts by half a pixel.
    className={`w-full flex items-center gap-3 px-3 py-2.5 3xl:px-4 3xl:py-3 rounded-[6px] text-[14px] 3xl:text-[16px] text-[#00424B] border-[0.5px] transition-colors ${
      active
        ? "bg-[#EFFBFD] border-[#5BD3E1] font-medium"
        : "border-transparent hover:bg-[#EFFBFD]/60"
    }`}
  >
    <span className="w-[18px] h-[18px] 3xl:w-[20px] 3xl:h-[20px] flex-shrink-0">
      {icon}
    </span>
    <span className="truncate">{label}</span>
    {/* Pushed to the far edge rather than sitting after the label, so the
        badges on a rail of items line up with each other instead of with the
        ends of the words. Red rather than the row tag's cyan, and still where
        that one catches the light: on a rail the eye passes over on the way to
        somewhere else, the colour is enough, and the row it leads to is where
        the flag can move. Capped, because past a point the digit has stopped
        being the news — see `useNewEnquiryCount`. */}
    {count > 0 && (
      <NewTag
        tone="count"
        className="ml-auto"
        label={`${count} new`}
      >
        {count > MAX_SHOWN_COUNT ? `${MAX_SHOWN_COUNT}+` : count}
      </NewTag>
    )}
  </button>
);

interface NavItem {
  path: string;
  label: string;
  /** SVGR component or a lucide icon - both take a className to size them. */
  Icon: NavIcon;
  /** What the item's badge shows, for the one item that carries one. */
  count?: number;
}

const Separator = () => (
  <div className="my-3 mx-1 border-t border-[#D7F9FD]" />
);

/**
 * The path the rail should light for, out of the router state a navigation
 * carried — or nothing, for the ordinary case where the URL speaks for itself.
 *
 * A screen can be reached from a section that is not the one its URL sits
 * under: a plan opened from a client's Plans tab lives at `/create-plan/:id`,
 * and lighting Create plan for it says the user left the client they are
 * plainly still inside. `origin` is the same state the plan builder reads for
 * its breadcrumb and its way back out (see `PlanDetailPage`), so the rail and
 * the trail agree on where the screen was entered from.
 *
 * Read defensively: state is whatever the last navigation put there, and a
 * screen opened by its URL alone has none. It survives a reload — the browser
 * keeps history state — so a refreshed plan keeps the section it came from.
 */
const originPath = (state: unknown): string | undefined => {
  const to = (state as { origin?: { to?: unknown } } | null)?.origin?.to;
  return typeof to === "string" ? to : undefined;
};

interface SidebarProps {
  /** Mobile drawer state. Ignored from `md` up, where the rail is static. */
  open: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // A rail item is the commonest way somebody leaves a half-filled form by
  // accident — it is a press away from anywhere in the app, and nothing on it
  // says the screen behind it is holding anything. So the navigation is handed
  // over rather than made: it happens straight away where there is nothing to
  // lose, and is asked about first where there is. See `UnsavedChanges`.
  const guardNavigation = useNavigationGuard();

  const { fullName, email, imageUrl } = useSignedInUser();

  // How many enquiries nobody has opened yet. The rail is the one place in the
  // app that says so without being on the list — which is the point: an
  // enquiry that has come in is news wherever the dietitian happens to be
  // working. Read here rather than on the list itself, so it is counted once
  // for the whole session. See `useNewEnquiryCount`.
  const newEnquiries = useNewEnquiryCount();

  // And how much there is to look at among the clients: one who has landed
  // since this browser started counting and nobody has opened, and answers
  // that have come back unread. Two arrivals under one number, because to the
  // person reading the rail they are the same errand — there is a client to go
  // and look at — and both are counted, so a form coming back moves the badge
  // even for a client it was already counting. See `useNewClientCount`.
  const newClients = useNewClientCount();
  const {
    subscription,
    trial,
    accessSource,
    status: subscriptionStatus,
  } = useCurrentSubscription();

  // `useSignedInUser` walks Clerk's fields down to the email address; the
  // generic is for the moment before Clerk has loaded, and it is only ever a
  // flash - the rail renders behind `RequireAuth`, which already has a session.
  const displayName = fullName || "your account";

  /**
   * The line under the name: the plan they are on, or — for everyone who is
   * not on one, the trial included — the address they are signed in as.
   *
   * It used to say "Subscribe now" where there was no plan, which is a claim
   * about somebody's account made in the one place that only has to say who is
   * reading; the tier itself is a fact, so that half stays. Nothing is claimed
   * until the read answers: the address is true whatever billing comes back
   * with, so it holds the line meanwhile rather than a plan name that might be
   * wrong.
   *
   * Where the account has no name at all, `fullName` is already the address,
   * and repeating it under itself says nothing — so the line stands empty
   * rather than doubling.
   */
  const isSubscribed =
    subscriptionStatus === "ready" && accessSource === "SUBSCRIPTION";
  const accountLine = isSubscribed
    ? PRODUCT_NAME
    : email && email !== displayName
    ? email
    : "";

  // Active item is derived from the URL, so deep links and browser history
  // highlight correctly without any extra state to keep in sync. Where a
  // navigation said which section it came from, that wins: the URL is where the
  // screen lives, and the origin is which part of the app the user is in.
  const activePath = originPath(location.state) ?? location.pathname;
  const isActive = (path: string) => activePath.startsWith(path);

  /**
   * Whether the fortnight is what has run out, which is what the card at the
   * foot of the rail says instead of a countdown.
   *
   * `!subscription` is doing real work: a workspace that took the trial, bought
   * a plan and later cancelled it also has a finished trial on its record, and
   * telling that dietitian their free trial has ended would name the wrong
   * thing by about a year. Same line `useSubscriptionGate` and the dashboard
   * draw, and only while the read has answered — the same caution the countdown
   * itself is held to.
   */
  const trialEnded =
    subscriptionStatus === "ready" &&
    !hasAccess(accessSource) &&
    !subscription &&
    !!trial;

  const iconClass = "w-full h-full";

  // Glyphs come from `NAV_ICONS`, which the not-yet-built screens read too, so
  // a destination is known by the same icon in the rail and on its own page.
  const items: (NavItem | "separator")[] = [
    {
      path: ROUTES.dashboard,
      label: "Dashboard",
      Icon: NAV_ICONS[ROUTES.dashboard],
    },
    {
      path: ROUTES.myClients,
      label: "My clients",
      Icon: NAV_ICONS[ROUTES.myClients],
      count: newClients,
    },
    {
      path: ROUTES.enquiries,
      label: "Enquiries",
      Icon: NAV_ICONS[ROUTES.enquiries],
      count: newEnquiries,
    },
    {
      path: ROUTES.createPlan,
      label: "Create plan",
      Icon: NAV_ICONS[ROUTES.createPlan],
    },
    {
      path: ROUTES.dishes,
      label: "Dishes",
      Icon: NAV_ICONS[ROUTES.dishes],
    },
    {
      path: ROUTES.revenue,
      label: "Revenue",
      Icon: NAV_ICONS[ROUTES.revenue],
    },
    "separator",
    {
      path: ROUTES.myBrand,
      label: "My brand",
      Icon: NAV_ICONS[ROUTES.myBrand],
    },
    {
      path: ROUTES.createPage,
      label: "Create page",
      Icon: NAV_ICONS[ROUTES.createPage],
    },
    {
      path: ROUTES.launchApp,
      label: "Launch app",
      Icon: NAV_ICONS[ROUTES.launchApp],
    },
    "separator",
    // Billing used to sit on its own between two rules here. It is a tab of
    // Settings now — what the practice pays Naurist is settled in the same
    // place as everything else about the account, rather than from a rail item
    // of its own.
    {
      path: ROUTES.settings,
      label: "Settings",
      Icon: NAV_ICONS[ROUTES.settings],
    },
  ];

  return (
    <>
      {/* Backdrop — only ever visible while the mobile drawer is open. */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={`bg-[#F8FEFF] border-r-[0.5px] border-[#A6EDF5] flex flex-col overflow-hidden
          fixed inset-y-0 left-0 z-40 w-[240px] transition-transform duration-300
          ${open ? "translate-x-0" : "-translate-x-full"}
          md:static md:translate-x-0 md:z-auto md:flex-shrink-0 md:h-full
          md:w-[180px] lg:w-[192px] 2xl:w-[210px] 3xl:w-[240px] 4xl:w-[280px]`}
      >
        <div className="flex-shrink-0 flex items-center justify-between px-4 md:px-5 3xl:px-6 pt-6 pb-2">
          <img src={logo} alt="Naurist" className="h-6 3xl:h-7 w-auto" />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            className="md:hidden p-1 rounded-md text-[#00424B] hover:bg-[#EFFBFD]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto sidebar-nav-scroll px-3 3xl:px-4 mt-4 pb-2">
          <div className="flex flex-col gap-1">
            {items.map((item, index) =>
              item === "separator" ? (
                <Separator key={`sep-${index}`} />
              ) : (
                <SidebarItem
                  key={item.path}
                  icon={<item.Icon className={iconClass} />}
                  label={item.label}
                  active={isActive(item.path)}
                  count={item.count}
                  // Guarded even where the item is the lit one: a form lives
                  // under the section it belongs to, so Add client is reached
                  // with My clients already active, and pressing it is still
                  // leaving the form.
                  onClick={() => guardNavigation(() => navigate(item.path))}
                />
              )
            )}
          </div>
        </nav>

        {/* Account block, pinned to the bottom: who is signed in, and what
            they are on. It carries no action of its own — signing out is on
            the Settings screen's Accounts tab, with the rest of what can be
            done to an account. */}
        <div className="flex-shrink-0 px-4 3xl:px-5 py-4">
          {/* How much of the free trial is left, over the account it belongs
              to — or, at the end of it, the way onto a plan.

              Both are drawn and exactly one of them appears: the meter holds
              the calm stretch of the fortnight and stands down for the last
              few days, where the card takes over and stays past the end date.
              Which day that is is `TRIAL_URGENT_DAYS`, and neither component
              decides it alone. Only once the read has answered, either way: a
              countdown drawn from a state we do not know yet is a claim about
              somebody's account, the same caution `planLine` takes. A
              workspace on a paid plan has neither to draw and sees nothing
              here. */}
          <TrialMeter trial={subscriptionStatus === "ready" ? trial : null} />
          <TrialBanner
            trial={subscriptionStatus === "ready" ? trial : null}
            ended={trialEnded}
          />

          <div className="flex items-center gap-3">
            <div className="w-9 h-9 3xl:w-10 3xl:h-10 rounded-full bg-[#EDE7F6] flex items-center justify-center flex-shrink-0 overflow-hidden">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-sm 3xl:text-base font-semibold text-[#5B3FA8]">
                  {displayName.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] 3xl:text-[15px] font-medium text-ink truncate">
                {displayName}
              </div>
              {/* The tier, or the address — see `accountLine`. The tier
                  keeps the brand teal it has always had; the address is grey
                  and quiet, since it names the account rather than telling
                  them anything about it, and the name above is what the eye
                  should land on. Neither leads anywhere: both are facts. A
                  non-breaking space where there is nothing to print, so the
                  block keeps its height either way. */}
              <div
                className={`text-[11px] 3xl:text-[13px] truncate ${
                  isSubscribed ? "text-[#00BACB] font-medium" : "text-[#8B8D98]"
                }`}
              >
                {accountLine || "\u00a0"}
              </div>
            </div>
          </div>
        </div>
      </aside>

    </>
  );
};

export default Sidebar;
